import { formatDate } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { DashboardCard } from '../class/Preference';
import { fuelstats, ServiceData, TripStats, TripstatsData } from '../class/TripStats';
import ImperialPipe from '../utils/pipes/imperial.pipe';
import { ApiService } from './api.service';
import { BaseService } from './base.service';
import { DevicesManagerService } from './devices-manager.service';
import { MapService } from './map.service';
import { PreferenceService } from './preference.service';
import UnitsService from './units.service';

interface ResponseData {
  mileage?: number;
  stops?: number;
  engine?: number;
  trip_time?: number;
  speed_avg?: number;
  uid_device?: number;
  registration?: string;
  uid_service?: number;
  label?: string;
  time_unit?: string;
  time_value?: string;
  year?: number;
  eco_avg?: number;
  fuel_quantity_100_km?: number;
  fuel_cost_100_km?: number;
  CO2_100_km?: number;
  fuel_consumed?: number;
  timestamp?: number;
}

@Injectable({
  providedIn: 'root',
})
export class TripStatsService extends BaseService<TripstatsData> {
  serviceData: ServiceData = {};
  public chosenService: number;
  public subservices: number[] = [];
  public allSubservicesDevices: number[] = [];
  public $config = signal<DashboardCard>(undefined);
  public index: number;

  private _unitsService = inject(UnitsService);
  private _devicesManagerService = inject(DevicesManagerService);
  private preferenceService = inject(PreferenceService);
  private imperialPipe = inject(ImperialPipe);
  private translate = inject(TranslateService);
  private mapService = inject(MapService);

  public $isEditChart = signal<boolean>(false);
  public $isFullscreen = signal<boolean>(false);
  private currentLocale = this.preferenceService.$currentLang();

  constructor() {
    super(inject(ApiService));
  }

  // uid_service of the request currently in flight; HandleResponse reads it from here
  // since BaseService only forwards the raw response, not per-call context.
  private pendingUidParent: number;

  public override HandleResponse(response: JSON): TripstatsData {
    let uid_parent = this.pendingUidParent;
    let resp: ResponseData[];
    if (response?.['tripstats']?.['data']) {
      resp = response?.['tripstats']?.['data'];
    } else if (Array.isArray(response) && response?.length > 0) {
      resp = response;
    }
    const timestamp = new Date().getTime() / 1000;

    const tripStats = new TripStats(
      [],
      [],
      [],
      [],
      [],
      [],
      [],
      [],
      [],
      undefined,
      [],
      [],
      [],
      [],
      [],
      [],
      [],
      undefined,
      timestamp
    );
    if (uid_parent === null || uid_parent === undefined || (resp[0] && resp[0].uid_service !== undefined)) {
      for (const r of resp) {
        const mileage = Number(this.imperialPipe.transform(r.mileage, 'distance', 0, true));
        tripStats.mileage.push(mileage);
        tripStats.stops.push(r.stops);
        tripStats.engine.push(Math.round(r.engine / 3600));
        tripStats.trip_time.push(Math.round(r.trip_time / 3600));
        const speedAvg = this.imperialPipe.transform(
          r.speed_avg,
          this.preferenceService.$preference().config.speedUnit,
          0,
          true
        );
        tripStats.speed_avg.push(Number(speedAvg));
        tripStats.eco_avg.push(Math.round(r.eco_avg * 100) / 100);
        tripStats.fuel_quantity_100_km.push(Math.round(r.fuel_quantity_100_km));
        tripStats.fuel_cost_100_km.push(Math.round(r.fuel_cost_100_km));
        tripStats.CO2_100_km.push(Math.round(r.CO2_100_km));
        tripStats.fuel_consumed.push(Math.round(r.fuel_consumed));
        if (r.uid_device) {
          tripStats.uid_device.push(r.uid_device);
          tripStats.registration.push(r.registration === null ? r.uid_device.toString() : r.registration);
        }
        if (r.uid_service) {
          tripStats.uid_service.push(r.uid_service);
          tripStats.label.push(r.label);
        }
        if (resp[0].time_unit) {
          tripStats.time_unit = resp[0].time_unit;
          if (resp[0].time_unit === 'month') {
            tripStats.time_value.push(this.getMonth(Number(r.time_value)) + ' ' + r.year);
          } else if (resp[0].time_unit === 'week') {
            tripStats.time_value.push(r.time_value + ' / ' + r.year);
          } else if (resp[0].time_unit === 'day') {
            tripStats.time_value.push(formatDate(r.time_value, 'shortDate', this.currentLocale));
          }
          tripStats.year.push(r.year);
        }
      }
      // ADD condition to push new data in tripstats.service and label
      if (uid_parent === null || uid_parent === undefined) {
        this.allSubservicesDevices = [];
        this.getSubservicesDevices(0);
        for (const device of this.allSubservicesDevices) {
          if (!tripStats.uid_device.includes(device)) {
            tripStats.mileage.push(0);
            tripStats.stops.push(0);
            tripStats.engine.push(0);
            tripStats.trip_time.push(0);
            tripStats.speed_avg.push(0);
            tripStats.uid_device.push(device);
            tripStats.registration.push(
              this._devicesManagerService.get(device).registration === null
                ? device.toString()
                : this._devicesManagerService.get(device).registration
            );
            tripStats.label.push();
            tripStats.time_value.push();
            tripStats.year.push();
            tripStats.eco_avg.push();
            tripStats.fuel_quantity_100_km.push();
            tripStats.fuel_cost_100_km.push();
            tripStats.CO2_100_km.push();
            tripStats.fuel_consumed.push();
          }
        }
      }
    } else {
      this.allSubservicesDevices = [];
      this.getSubservicesDevices(uid_parent);
      for (const r of resp) {
        if (this.allSubservicesDevices.includes(r.uid_device) || r.year) {
          const mileage = Number(this.imperialPipe.transform(r.mileage, 'distance', 0, true));
          tripStats.mileage.push(Number(mileage));
          tripStats.stops.push(r.stops);
          tripStats.engine.push(Math.round(r.engine / 3600));
          tripStats.trip_time.push(Math.round(r.trip_time / 3600));
          const speedAvg = this.imperialPipe.transform(
            r.speed_avg,
            this.preferenceService.$preference().config.speedUnit,
            0,
            true
          );
          tripStats.speed_avg.push(Number(speedAvg));
          tripStats.eco_avg.push(Math.round(r.eco_avg * 100) / 100);
          tripStats.fuel_quantity_100_km.push(Math.round(r.fuel_quantity_100_km));
          tripStats.fuel_cost_100_km.push(Math.round(r.fuel_cost_100_km));
          tripStats.CO2_100_km.push(Math.round(r.CO2_100_km));
          tripStats.fuel_consumed.push(Math.round(r.fuel_consumed));
          if (r.uid_device) {
            tripStats.uid_device.push(r.uid_device);
            tripStats.registration.push(r.registration === null ? r.uid_device.toString() : r.registration);
          }
          if (r.uid_service) {
            tripStats.uid_service.push(r.uid_service);
            tripStats.label.push(r.label);
          }
          if (resp[0].time_unit) {
            tripStats.time_unit = resp[0].time_unit;
            if (resp[0].time_unit === 'month') {
              tripStats.time_value.push(this.getMonth(Number(r.time_value)) + ' ' + r.year);
            } else if (resp[0].time_unit === 'week') {
              tripStats.time_value.push(r.time_value + ' / ' + r.year);
            } else if (resp[0].time_unit === 'day') {
              tripStats.time_value.push(formatDate(r.time_value, 'shortDate', this.currentLocale));
            }
            tripStats.year.push(r.year);
          }
        }
      }

      for (const device of this.allSubservicesDevices) {
        if (!tripStats.uid_device.includes(device)) {
          tripStats.mileage.push(0);
          tripStats.stops.push(0);
          tripStats.engine.push(0);
          tripStats.trip_time.push(0);
          tripStats.speed_avg.push(0);
          tripStats.uid_device.push(device);
          tripStats.registration.push(
            this._devicesManagerService.get(device).registration === null
              ? device.toString()
              : this._devicesManagerService.get(device).registration
          );
          tripStats.label.push();
          tripStats.time_value.push();
          tripStats.year.push();
          tripStats.eco_avg.push(0);
          tripStats.fuel_quantity_100_km.push(0);
          tripStats.fuel_cost_100_km.push(0);
          tripStats.CO2_100_km.push(0);
          tripStats.fuel_consumed.push(0);
        }
      }
    }

    if (resp[0] && resp[0].uid_service) {
      this.setServiceData(tripStats);
    } else {
      if (uid_parent === null || uid_parent === undefined) {
        uid_parent = 0;
      }
      this.getSubservicesDevices(uid_parent);
      this.getSubservices(uid_parent);
      this.setDeviceData(tripStats);
    }

    const data: {
      tripstats: TripStats;
      servicedata: ServiceData;
    } = {
      tripstats: tripStats,
      servicedata: this.serviceData,
    };

    return data;
  }

  public getSubservices(uid_service: number) {
    this.subservices = this._unitsService
      .getSubUnits(uid_service, { directChildrenOnly: true })
      .map(unit => unit.uid);
  }

  private setDeviceData(tripStats: TripStats) {
    this.serviceData = {};
    for (const i in this._devicesManagerService.$devicesMap()) {
      if (this.allSubservicesDevices.includes(Number(i))) {
        this.calcDeviceTotals(tripStats, Number(i));
      }
    }
  }

  private calcDeviceTotals(tripstats: TripStats, id: number) {
    if (!this.serviceData[id]) {
      this.serviceData[id] = {
        mileageTotal: 0,
        stopsTotal: 0,
        engineTotal: 0,
        trip_timeTotal: 0,
        speed_avgTotal: 0,
        speed_avgAverage: 0,
        eco_avgTotal: 0,
        eco_avgAverage: 0,
        fuel_quantity_100_kmTotal: 0,
        fuel_cost_100_kmTotal: 0,
        CO2_100_kmTotal: 0,
        fuel_consumedTotal: 0,
        mileage: [],
        stops: [],
        engine: [],
        trip_time: [],
        speed_avg: [],
        uid_service: [],
        label: [],
        eco_avg: [],
        fuel_quantity_100_km: [],
        fuel_cost_100_km: [],
        CO2_100_km: [],
        fuel_consumed: [],
      };
    }

    const arrayPosition: number = tripstats.uid_device.indexOf(id);

    if (arrayPosition !== -1) {
      this.serviceData[id].mileageTotal += tripstats.mileage[arrayPosition];
      this.serviceData[id].stopsTotal += tripstats.stops[arrayPosition];
      this.serviceData[id].engineTotal += tripstats.engine[arrayPosition];
      this.serviceData[id].trip_timeTotal += tripstats.trip_time[arrayPosition];
      this.serviceData[id].speed_avgTotal += tripstats.speed_avg[arrayPosition];
      this.serviceData[id].eco_avgTotal += tripstats.eco_avg[arrayPosition];
      this.serviceData[id].fuel_quantity_100_kmTotal += tripstats.fuel_quantity_100_km[arrayPosition];
      this.serviceData[id].fuel_cost_100_kmTotal += tripstats.fuel_cost_100_km[arrayPosition];
      this.serviceData[id].CO2_100_kmTotal += tripstats.CO2_100_km[arrayPosition];
      this.serviceData[id].fuel_consumedTotal += tripstats.fuel_consumed[arrayPosition];
    }
  }

  private setServiceData(tripStats: TripStats) {
    this.serviceData = {};
    const units = this._unitsService.$values() ?? [];
    this.calcServiceTotals(tripStats, 0);
    for (const unit of units) {
      this.calcServiceTotals(tripStats, unit.uid);
    }
    this.listTotalsByService();
  }

  private calcServiceTotals(tripstats: TripStats, id: number) {
    if (!this.serviceData[id]) {
      this.serviceData[id] = {
        mileageTotal: 0,
        stopsTotal: 0,
        engineTotal: 0,
        trip_timeTotal: 0,
        speed_avgTotal: 0,
        speed_avgAverage: 0,
        eco_avgTotal: 0,
        eco_avgAverage: 0,
        fuel_quantity_100_kmTotal: 0,
        fuel_cost_100_kmTotal: 0,
        CO2_100_kmTotal: 0,
        fuel_consumedTotal: 0,
        mileage: [],
        stops: [],
        engine: [],
        trip_time: [],
        speed_avg: [],
        uid_service: [],
        label: [],
        eco_avg: [],
        fuel_quantity_100_km: [],
        fuel_cost_100_km: [],
        CO2_100_km: [],
        fuel_consumed: [],
      };
    }
    const arrayPosition: number = tripstats.uid_service.indexOf(id);

    if (arrayPosition !== -1) {
      this.serviceData[id].mileageTotal += tripstats.mileage[arrayPosition];
      this.serviceData[id].stopsTotal += tripstats.stops[arrayPosition];
      this.serviceData[id].engineTotal += tripstats.engine[arrayPosition];
      this.serviceData[id].trip_timeTotal += tripstats.trip_time[arrayPosition];
      this.serviceData[id].speed_avgTotal += tripstats.speed_avg[arrayPosition];
      this.serviceData[id].eco_avgTotal += tripstats.eco_avg[arrayPosition];
      this.serviceData[id].fuel_quantity_100_kmTotal += tripstats.fuel_quantity_100_km[arrayPosition];
      this.serviceData[id].fuel_cost_100_kmTotal += tripstats.fuel_cost_100_km[arrayPosition];
      this.serviceData[id].CO2_100_kmTotal += tripstats.CO2_100_km[arrayPosition];
      this.serviceData[id].fuel_consumedTotal += tripstats.fuel_consumed[arrayPosition];

      this.addDataToParents(
        id,
        tripstats.mileage[arrayPosition],
        tripstats.stops[arrayPosition],
        tripstats.engine[arrayPosition],
        tripstats.trip_time[arrayPosition],
        tripstats.speed_avg[arrayPosition],
        tripstats.eco_avg[arrayPosition],
        tripstats.fuel_quantity_100_km[arrayPosition],
        tripstats.fuel_cost_100_km[arrayPosition],
        tripstats.CO2_100_km[arrayPosition],
        tripstats.fuel_consumed[arrayPosition]
      );
    }
  }

  private addDataToParents(
    serviceId: number,
    mileage: number,
    stops: number,
    engine: number,
    trip_time: number,
    speed_avg: number,
    eco_avg: number,
    fuel_quantity_100_km: number,
    fuel_cost_100_km: number,
    CO2_100_km: number,
    fuel_consumed: number
  ) {
    const parentId = this._unitsService.$parentById()?.[serviceId];
    if (parentId != null) {
      if (this.serviceData[parentId]) {
        this.serviceData[parentId].mileageTotal += mileage;
        this.serviceData[parentId].stopsTotal += stops;
        this.serviceData[parentId].engineTotal += engine;
        this.serviceData[parentId].trip_timeTotal += trip_time;
        this.serviceData[parentId].speed_avgTotal += speed_avg;
        this.serviceData[parentId].eco_avgTotal += eco_avg;
        this.serviceData[parentId].fuel_quantity_100_kmTotal += fuel_quantity_100_km;
        this.serviceData[parentId].fuel_cost_100_kmTotal += fuel_cost_100_km;
        this.serviceData[parentId].CO2_100_kmTotal += CO2_100_km;
        this.serviceData[parentId].fuel_consumedTotal += fuel_consumed;
      } else {
        this.serviceData[parentId] = {
          mileageTotal: 0,
          stopsTotal: 0,
          engineTotal: 0,
          trip_timeTotal: 0,
          speed_avgTotal: 0,
          speed_avgAverage: 0,
          eco_avgTotal: 0,
          eco_avgAverage: 0,
          fuel_quantity_100_kmTotal: 0,
          fuel_cost_100_kmTotal: 0,
          CO2_100_kmTotal: 0,
          fuel_consumedTotal: 0,
        };
        this.serviceData[parentId].mileageTotal = mileage;
        this.serviceData[parentId].stopsTotal = stops;
        this.serviceData[parentId].engineTotal = engine;
        this.serviceData[parentId].trip_timeTotal = trip_time;
        this.serviceData[parentId].speed_avgTotal = speed_avg;
        this.serviceData[parentId].eco_avgTotal = eco_avg;
        this.serviceData[parentId].fuel_quantity_100_kmTotal = fuel_quantity_100_km;
        this.serviceData[parentId].fuel_cost_100_kmTotal = fuel_cost_100_km;
        this.serviceData[parentId].CO2_100_kmTotal = CO2_100_km;
        this.serviceData[parentId].fuel_consumedTotal = fuel_consumed;
      }
    }
    if (parentId && serviceId) {
      this.addDataToParents(
        parentId,
        mileage,
        stops,
        engine,
        trip_time,
        speed_avg,
        eco_avg,
        fuel_quantity_100_km,
        fuel_cost_100_km,
        CO2_100_km,
        fuel_consumed
      );
    }
  }

  /**
   * Computes, for every service, the total number of its descendant services
   * (direct children, grand-children, ...). Used as the divisor to average the
   * accumulated speed/eco totals in listTotalsByService.
   *
   * Replaces the former recursive ProcessingService.countServices: a single
   * memoized post-order traversal so each service is visited only once.
   */
  private countDescendantServices(): Record<number, number> {
    const counts: Record<number, number> = {};

    const count = (serviceId: number): number => {
      if (counts[serviceId] !== undefined) {
        return counts[serviceId];
      }
      const children =
        serviceId === 0
          ? this._unitsService.getRootUnits()
          : this._unitsService.getSubUnits(serviceId, { directChildrenOnly: true });
      let total = 0;
      for (const child of children) {
        total += 1 + count(child.uid);
      }
      counts[serviceId] = total;
      return total;
    };

    count(0);
    for (const unit of this._unitsService.$values() ?? []) {
      count(unit.uid);
    }
    return counts;
  }

  private listTotalsByService() {
    const descendantServicesCount = this.countDescendantServices();
    const allIds = [0, ...(this._unitsService.$values() ?? []).map(unit => unit.uid)];

    for (const p of allIds) {
      const children =
        p === 0
          ? this._unitsService.getRootUnits()
          : this._unitsService.getSubUnits(p, { directChildrenOnly: true });
      const parentData = this.serviceData[p];

      if (children.length > 0) {
        for (const child of children) {
          const c = child.uid;
          const childData = this.serviceData[c];
          const totalService = descendantServicesCount[c] > 0 ? descendantServicesCount[c] : 1;

          const speedAvg = childData.speed_avgTotal / totalService;
          const ecoAvg = childData.eco_avgTotal / totalService;

          if (parentData && parentData.uid_service) {
            parentData.uid_service.push(c);
            parentData.label.push(child.label);
            parentData.mileage.push(childData.mileageTotal);
            parentData.stops.push(childData.stopsTotal);
            parentData.engine.push(childData.engineTotal);
            parentData.trip_time.push(childData.trip_timeTotal);
            parentData.fuel_quantity_100_km.push(childData.fuel_quantity_100_kmTotal);
            parentData.fuel_cost_100_km.push(childData.fuel_cost_100_kmTotal);
            parentData.CO2_100_km.push(childData.CO2_100_kmTotal);
            parentData.speed_avg.push(speedAvg);
            parentData.eco_avg.push(ecoAvg);
            parentData.fuel_consumed.push(childData.fuel_consumedTotal);
          } else {
            this.serviceData[p] = {
              ...parentData,
              uid_service: [c],
              label: [child.label],
              mileage: [childData.mileageTotal],
              stops: [childData.stopsTotal],
              engine: [childData.engineTotal],
              trip_time: [childData.trip_timeTotal],
              fuel_quantity_100_km: [childData.fuel_quantity_100_kmTotal],
              fuel_cost_100_km: [childData.fuel_cost_100_kmTotal],
              CO2_100_km: [childData.CO2_100_kmTotal],
              speed_avg: [speedAvg],
              eco_avg: [ecoAvg],
              fuel_consumed: [childData.fuel_consumedTotal],
            };
          }
        }
      } else {
        this.serviceData[p] = {
          ...parentData,
          mileage: [parentData.mileageTotal],
          stops: [parentData.stopsTotal],
          engine: [parentData.engineTotal],
          trip_time: [parentData.trip_timeTotal],
          speed_avg: [parentData.speed_avgTotal],
          eco_avg: [parentData.eco_avgTotal],
          fuel_quantity_100_km: [parentData.fuel_quantity_100_kmTotal],
          fuel_cost_100_km: [parentData.fuel_cost_100_kmTotal],
          CO2_100_km: [parentData.CO2_100_kmTotal],
          fuel_consumed: [parentData.fuel_consumedTotal],
        };
      }
    }
  }

  private getMonth(month: number) {
    return this.translate.instant('DATE.MONTHS.' + month);
  }

  public loadTripStats(
    token: string,
    from: number,
    to: number,
    data: string,
    group?: string,
    uid_service?: number
  ): Observable<TripstatsData> {
    let activeSubservice = false;
    const params = {
      token: token,
      from: from,
      to: to,
      data: data,
    };
    if (group) {
      params['group'] = group;
    }
    if (uid_service) {
      // Check if there are subservices
      if (this.subservices.length > 0) {
        // If there is, get devices from this subservices
        this.allSubservicesDevices = [];
        this.getSubservicesDevices(this.chosenService);
        activeSubservice = true;
      }
    }
    // only add uid_service param if not grouping by service

    if (uid_service && params['group'] !== 'service' && !activeSubservice) {
      params['uid_service'] = uid_service;
    }
    const isFuelstats = fuelstats.includes(params?.data);
    this.pendingUidParent = uid_service;
    // fuelstats is a v2-only endpoint whose args must stay unencoded (json: true)
    return this.Get(
      isFuelstats ? 'fuelstats' : 'tripstats',
      params,
      isFuelstats ? true : undefined,
      isFuelstats ? 'v2' : undefined
    );
  }

  public getSubservicesDevices(uid_service: number) {
    if (uid_service) {
      for (const device of this._unitsService.getDevices(uid_service, { directChildrenOnly: true })) {
        this.allSubservicesDevices.push(device.uid);
      }
      for (const service of this._unitsService.getSubUnits(uid_service, { directChildrenOnly: true })) {
        this.getSubservicesDevices(service.uid);
      }
    }
  }

  public updateConfig(config: DashboardCard) {
    this.$config.set(config);
  }

  public resizeChart(value: boolean, index: number, config: DashboardCard) {
    this.$isFullscreen.set(value);
    if (value) {
      this.mapService.tempMap = document.getElementById('map');
      this.index = index;
      this.updateConfig(config);
    } else {
      this.$isEditChart.set(false);
      this.index = undefined;
      this.updateConfig(undefined);

      setTimeout(() => {
        if (document.getElementById('dash-map') && this.mapService.tempMap) {
          document.getElementById('dash-map').appendChild(this.mapService.tempMap);
        }
        this.mapService.tempMap = undefined;

        const element = document.getElementById(`chart-${index}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }
}
